import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUserAuth } from "../context/UserAuthContext";
import { userApi } from "../lib/api";
import { User, Package, Heart, LogOut, ArrowLeft, Trash2 } from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { productPath } from "../lib/productSlug";

const STATUS_KEY = {
  pending: "status.pending",
  shipped: "status.shipped",
  delivered: "status.delivered",
  cancelled: "status.cancelled",
};

const ProfileTab = ({ user, onUpdated }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: user.name || "",
    password: "",
    shipping_address: user.shipping_address || "",
    city: user.city || "",
    postal_code: user.postal_code || "",
    country: user.country || "Italia",
  });
  const [saving, setSaving] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await userApi.put("/users/me", payload);
      toast.success(t("account.saved"));
      setForm((f) => ({ ...f, password: "" }));
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="profile-form" className="space-y-6 max-w-2xl">
      {/* Personal */}
      <div className="bg-zinc-900 border-2 border-zinc-800 p-6 space-y-4">
        <h3 className="font-heading uppercase text-yellow-400 text-lg tracking-widest">{t("account.section_personal")}</h3>
        <div>
          <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Email</label>
          <input disabled value={user.email} className="w-full bg-zinc-950 border-2 border-zinc-800 px-4 py-2 text-zinc-500 font-mono" />
        </div>
        <div>
          <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">{t("auth.name")}</label>
          <input data-testid="profile-name" required value={form.name} onChange={onChange("name")}
            className="w-full bg-zinc-950 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none" />
        </div>
        <div>
          <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">{t("account.new_password")}</label>
          <input data-testid="profile-password" type="password" minLength={6} value={form.password} onChange={onChange("password")}
            className="w-full bg-zinc-950 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none font-mono" />
        </div>
      </div>

      {/* Address */}
      <div className="bg-zinc-900 border-2 border-zinc-800 p-6 space-y-4">
        <div>
          <h3 className="font-heading uppercase text-yellow-400 text-lg tracking-widest">{t("account.section_address")}</h3>
          <p className="text-zinc-500 text-xs mt-1">{t("account.address_hint")}</p>
        </div>
        <div>
          <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">{t("checkout.address")}</label>
          <input data-testid="profile-address" value={form.shipping_address} onChange={onChange("shipping_address")}
            className="w-full bg-zinc-950 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">{t("checkout.city")}</label>
            <input data-testid="profile-city" value={form.city} onChange={onChange("city")}
              className="w-full bg-zinc-950 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none" />
          </div>
          <div>
            <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">{t("checkout.postal")}</label>
            <input data-testid="profile-postal" value={form.postal_code} onChange={onChange("postal_code")}
              className="w-full bg-zinc-950 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">{t("checkout.country")}</label>
          <input data-testid="profile-country" value={form.country} onChange={onChange("country")}
            className="w-full bg-zinc-950 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none" />
        </div>
      </div>

      <button data-testid="profile-save" disabled={saving} type="submit"
        className="bg-yellow-400 text-black font-heading uppercase px-6 py-3 hover:bg-white transition disabled:opacity-40">
        {saving ? "..." : t("account.save")}
      </button>
    </form>
  );
};

const OrdersTab = () => {
  const { t, i18n } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    userApi.get("/users/me/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-zinc-500 uppercase">...</div>;
  if (orders.length === 0) return <div className="text-zinc-500 uppercase" data-testid="no-orders">{t("account.no_orders")}</div>;

  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} data-testid={`my-order-${o.id}`} className="bg-zinc-900 border-2 border-zinc-800 p-5">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="font-jp text-yellow-400 text-[10px] tracking-widest">{t("account.order_id")}{o.id.slice(0, 8).toUpperCase()}</div>
              <div className="text-zinc-400 text-sm">{new Date(o.created_at).toLocaleDateString(i18n.language)}</div>
            </div>
            <div className="text-right">
              <div className="font-heading text-2xl text-yellow-400">€ {o.total.toFixed(2)}</div>
              <div className="text-xs uppercase tracking-widest text-zinc-400">{t(STATUS_KEY[o.status] || o.status)}</div>
            </div>
          </div>
          <ul className="space-y-1 text-sm border-t border-zinc-800 pt-3">
            {o.items.map((it, i) => (
              <li key={i} className="flex justify-between text-zinc-300">
                <span>{it.quantity}× {it.title}</span>
                <span className="text-yellow-400">€ {(it.price * it.quantity).toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

const WishlistTab = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await userApi.get("/users/me/wishlist");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await userApi.delete(`/users/me/wishlist/${id}`);
    toast.success(t("product.wishlist_removed"));
    load();
  };

  if (loading) return <div className="text-zinc-500 uppercase">...</div>;
  if (items.length === 0) return <div className="text-zinc-500 uppercase" data-testid="no-wishlist">{t("account.no_wishlist")}</div>;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((p) => (
        <div key={p.id} data-testid={`wishlist-item-${p.id}`} className="bg-zinc-900 border-2 border-zinc-800 hover:border-yellow-400 transition group">
          <div className="aspect-square overflow-hidden cursor-pointer" onClick={() => navigate(productPath(p))}>
            <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition duration-500" />
          </div>
          <div className="p-4 border-t-2 border-zinc-800">
            <div className="font-heading uppercase text-white">{p.title}</div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-yellow-400 font-bold">€ {p.price.toFixed(2)}</span>
              <button data-testid={`wishlist-remove-${p.id}`} onClick={() => remove(p.id)}
                className="bg-zinc-800 text-red-400 p-2 hover:bg-red-600 hover:text-white transition">
                <Trash2 className="w-4 h-4" strokeWidth={3} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AccountPage() {
  const { user, loading, logout, refresh } = useUserAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState("profile");
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500 uppercase">...</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/account" }} replace />;

  const tabs = [
    { key: "profile", label: t("account.tab_profile"), icon: User },
    { key: "orders", label: t("account.tab_orders"), icon: Package },
    { key: "wishlist", label: t("account.tab_wishlist"), icon: Heart },
  ];

  return (
    <div className="bg-zinc-950 min-h-screen text-white">
      <Navbar />
      <main className="pt-28 pb-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="text-zinc-500 hover:text-yellow-400 text-xs uppercase tracking-widest flex items-center gap-2 mb-6">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>

        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="font-jp text-yellow-400 text-xs tracking-widest mb-2">{t("account.eyebrow")}</div>
            <h1 className="font-heading text-5xl uppercase text-white leading-none">{t("account.title")}</h1>
            <p className="text-zinc-400 mt-2">{user.name} · <span className="text-yellow-400">{user.email}</span></p>
          </div>
          <button data-testid="user-logout" onClick={() => { logout(); navigate("/"); }}
            className="bg-red-600/10 border-2 border-red-600/40 text-red-400 hover:bg-red-600 hover:text-white font-heading uppercase tracking-widest px-5 py-3 flex items-center gap-2 transition">
            <LogOut className="w-4 h-4" strokeWidth={3} /> {t("nav.logout")}
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b-2 border-zinc-800">
          {tabs.map((tt) => {
            const Icon = tt.icon;
            return (
              <button key={tt.key} data-testid={`tab-${tt.key}`} onClick={() => setTab(tt.key)}
                className={`flex items-center gap-2 px-5 py-3 font-heading uppercase tracking-widest text-sm border-b-4 -mb-0.5 transition ${
                  tab === tt.key ? "border-yellow-400 text-yellow-400" : "border-transparent text-zinc-400 hover:text-yellow-400"
                }`}>
                <Icon className="w-4 h-4" strokeWidth={3} /> {tt.label}
              </button>
            );
          })}
        </div>

        <div>
          {tab === "profile" && <ProfileTab user={user} onUpdated={refresh} />}
          {tab === "orders" && <OrdersTab />}
          {tab === "wishlist" && <WishlistTab />}
        </div>
      </main>
      <Footer />
    </div>
  );
}
